<template>
  <div>
    <base-header class="pb-6">
      <template #title>
        <div class="d-flex align-items-center">
          <base-button
            type="link"
            class="pl-0 pr-2 text-white"
            @click="$router.push({ name: 'ScheduleList' })"
          >
            <i class="fas fa-arrow-left"></i>
          </base-button>
          <h1 class="text-white mb-0">{{ schedule ? schedule.name : 'Schedule Details' }}</h1>
        </div>
      </template>
    </base-header>

    <b-container fluid class="mt--6">
      <div v-if="!schedule" class="text-center py-5">
        <b-spinner variant="primary" />
      </div>

      <template v-else>
        <!-- Schedule info card -->
        <b-card no-body class="shadow mb-4">
          <b-card-body>
            <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:12px;">
              <div>
                <h3 class="mb-1">{{ schedule.name }}</h3>
                <div class="d-flex flex-wrap" style="gap:8px;">
                  <b-badge :variant="schedule.type === 'ota' ? 'info' : 'primary'" class="px-2 py-1">
                    {{ schedule.type === 'ota' ? 'OTA Firmware' : 'Provision' }}
                  </b-badge>
                  <b-badge :variant="schedule.mode === 'auto' ? 'success' : 'secondary'" class="px-2 py-1">
                    <i :class="schedule.mode === 'auto' ? 'fas fa-bolt' : 'fas fa-hand-pointer'" class="mr-1"></i>
                    {{ schedule.mode }} mode
                  </b-badge>
                  <b-badge :variant="statusVariant(schedule.status)" class="px-2 py-1">
                    {{ schedule.status }}
                  </b-badge>
                </div>
              </div>
              <div class="d-flex" style="gap:8px;">
                <base-button
                  v-if="schedule.status === 'active' && schedule.mode === 'manual'"
                  type="primary"
                  size="sm"
                  :disabled="readyDevices.length === 0 || triggeringAll"
                  @click="triggerAll"
                >
                  <b-spinner v-if="triggeringAll" small class="mr-2" />
                  <i v-else class="fas fa-play mr-2"></i>
                  Trigger All Ready ({{ readyDevices.length }})
                </base-button>
                <base-button
                  v-if="schedule.status === 'active'"
                  type="outline-secondary"
                  size="sm"
                  @click="loadSchedule"
                >
                  <i class="fas fa-sync-alt"></i>
                </base-button>
              </div>
            </div>

            <!-- Progress summary -->
            <div class="mt-3">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-muted">
                  {{ summary.completed }} of {{ summary.total }} devices updated
                </small>
                <small class="text-muted">Deadline: {{ schedule.deadline_days }} days</small>
              </div>
              <b-progress :max="summary.total || 1" height="8px">
                <b-progress-bar :value="summary.completed" variant="success" />
                <b-progress-bar
                  :value="summary.in_progress"
                  :variant="schedule.status === 'cancelled' ? 'secondary' : 'primary'"
                  :animated="schedule.status !== 'cancelled'"
                />
                <b-progress-bar :value="summary.ready" variant="warning" />
                <b-progress-bar :value="summary.failed + summary.expired" variant="danger" />
              </b-progress>
              <div class="d-flex flex-wrap mt-2" style="gap:12px;">
                <small><span class="badge badge-success mr-1">&nbsp;</span>Completed: {{ summary.completed }}</small>
                <small><span class="badge badge-primary mr-1">&nbsp;</span>In Progress: {{ summary.in_progress }}</small>
                <small><span class="badge badge-warning mr-1">&nbsp;</span>Ready: {{ summary.ready }}</small>
                <small><span class="badge badge-secondary mr-1">&nbsp;</span>Pending: {{ summary.pending }}</small>
                <small><span class="badge badge-danger mr-1">&nbsp;</span>Failed/Expired: {{ summary.failed + summary.expired }}</small>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- Device entries table -->
        <b-card no-body class="shadow">
          <card-header title="Device Entries">
            <b-form-input
              v-model="search"
              placeholder="Search device ID..."
              size="sm"
              style="max-width:220px;"
            />
          </card-header>
          <div class="table-responsive">
            <el-table
              :data="filteredEntries"
              style="width: 100%"
            >
              <el-table-column prop="device_id" label="Device ID" sortable>
                <template #default="{ row }">
                  <a
                    href="#"
                    @click.prevent="openProgressModal(row)"
                    class="text-primary font-weight-bold"
                    style="cursor: pointer;"
                  >
                    {{ row.device_id }}
                  </a>
                </template>
              </el-table-column>
              <el-table-column label="Status" sortable prop="status">
                <template #default="{ row }">
                  <div class="d-flex align-items-center">
                    <i :class="entryStatusIcon(row.status)" class="mr-2" :style="{ color: entryStatusColor(row.status) }"></i>
                    <span>{{ row.status }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Charger Detected">
                <template #default="{ row }">
                  <small v-if="row.conditions_met_at" class="text-muted">
                    {{ formatDatetime(row.conditions_met_at) }}
                  </small>
                  <small v-else class="text-muted">—</small>
                </template>
              </el-table-column>
              <el-table-column label="Completed">
                <template #default="{ row }">
                  <small v-if="row.completed_at" class="text-muted">
                    {{ formatDatetime(row.completed_at) }}
                    <b-badge v-if="row.fulfilled_by === 'manual'" variant="secondary" class="ml-1">manual</b-badge>
                  </small>
                  <small v-else class="text-muted">—</small>
                </template>
              </el-table-column>
              <el-table-column label="Error">
                <template #default="{ row }">
                  <small v-if="row.error_message" class="text-danger" :title="row.error_message">
                    {{ truncate(row.error_message, 40) }}
                  </small>
                  <small v-else class="text-muted">—</small>
                </template>
              </el-table-column>
              <el-table-column label="Actions">
                <template #default="{ row }">
                  <div v-if="schedule.status === 'active' || schedule.status === 'completed'">
                    <!-- Manual mode: Trigger for ready devices -->
                    <base-button
                      v-if="schedule.mode === 'manual' && row.status === 'ready'"
                      size="sm"
                      type="primary"
                      :disabled="triggeringDevices.has(row.device_id)"
                      @click="triggerDevice(row.device_id)"
                    >
                      <b-spinner v-if="triggeringDevices.has(row.device_id)" small class="mr-1" />
                      <i v-else class="fas fa-play mr-1"></i>
                      Trigger
                    </base-button>

                    <!-- Pending info for manual mode -->
                    <small v-if="schedule.mode === 'manual' && row.status === 'pending'" class="text-muted">
                      Waiting for charger
                    </small>

                    <!-- Retry button for failed/expired (both modes) -->
                    <base-button
                      v-if="row.status === 'failed' || row.status === 'expired'"
                      size="sm"
                      type="outline-warning"
                      :disabled="retryingDevices.has(row.device_id)"
                      @click="retryDevice(row.device_id)"
                    >
                      <b-spinner v-if="retryingDevices.has(row.device_id)" small class="mr-1" />
                      <i v-else class="fas fa-redo-alt mr-1"></i>
                      Retry
                    </base-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </b-card>

        <!-- Modal for progress panel -->
            <!-- OTA – keep existing step panel -->
        <b-modal
          v-if="schedule.type === 'ota'"
          v-model="progressModalVisible"
          :title="`Update Progress: ${selectedDeviceId}`"
          size="lg"
          hide-footer
          @hidden="handleModalHidden"
        >
          <DeviceProgressPanel
            v-if="selectedDeviceId"
            :deviceId="selectedDeviceId"
            :entity="schedule.entity || $route.params.entity"
            :updateType="schedule.type"
            :status="selectedDeviceStatus"
          />
        </b-modal>

        <!-- Provision – full file‑level modal -->
        <ModalProvision
          v-if="progressModalVisible && schedule.type === 'provision'"
          :device-id="selectedDeviceId"
          :entity="schedule.entity || $route.params.entity"
          @close-modal="handleModalHidden"
        />

     
      </template>
    </b-container>
  </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import DeviceProgressPanel from '../../views/components/DeviceProgressPanel.vue'; // adjust path if needed
import ModalProvision from '../../views/components/ModalProvision.vue'; // adjust path if needed
export default {
  name: 'ScheduleDetail',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    DeviceProgressPanel,
    ModalProvision,   
  },
  data() {
    return {
      search: '',
      triggeringDevices: new Set(),
      triggeringAll: false,
      pollTimer: null,
      progressModalVisible: false,
      selectedDeviceId: null,
      selectedDeviceStatus: null,
      retryingDevices: new Set(),
    };
  },
  computed: {
    schedule() {
      return this.$store.getters.activeSchedule;
    },
    summary() {
      return this.schedule?.summary || {};
    },
    filteredEntries() {
      const entries = this.schedule?.device_entries || [];
      if (!this.search) return entries;
      const q = this.search.toLowerCase();
      return entries.filter(e => e.device_id.toLowerCase().includes(q));
    },
    readyDevices() {
      return (this.schedule?.device_entries || []).filter(e => e.status === 'ready');
    },
  },
  mounted() {
    this.loadSchedule();
    this.pollTimer = setInterval(() => {
      if (this.schedule?.status === 'active') this.loadSchedule();
    }, 10000);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
  },
  methods: {
    async loadSchedule() {
      await this.$store.dispatch('schedule/get', this.$route.params.id);
    },
    openProgressModal(row) {
      this.selectedDeviceId = row.device_id;
      this.selectedDeviceStatus = row.status;
      this.progressModalVisible = true;
    },
    handleModalHidden() {
        this.progressModalVisible = false; 
      this.selectedDeviceId = null;
      this.selectedDeviceStatus = null;
    },
    async triggerDevice(deviceId) {
      this.triggeringDevices = new Set([...this.triggeringDevices, deviceId]);
      try {
        await this.$store.dispatch('schedule/triggerDevice', {
          scheduleId: this.schedule.id,
          deviceId,
        });
        await this.loadSchedule();
      } catch (err) {
        // Error toast handled by Vuex action
      } finally {
        this.triggeringDevices = new Set([...this.triggeringDevices].filter(id => id !== deviceId));
      }
    },
    async retryDevice(deviceId) {
  this.retryingDevices = new Set([...this.retryingDevices, deviceId]);
  try {
    await this.$http.post(
      `/tenant/${this.schedule.entity}/schedule/${this.schedule.id}/device/${deviceId}/retry`
    );
    await this.loadSchedule();
  } catch (err) {
    console.error("Retry failed", err);
    this.$notify({
      title: "Error",
      message: err.response?.data?.error || err.message,
      type: "error",
    });
  } finally {
    this.retryingDevices = new Set(
      [...this.retryingDevices].filter(id => id !== deviceId)
    );
  }
},
    async triggerAll() {
      this.triggeringAll = true;
      try {
        await Promise.all(
          this.readyDevices.map(e =>
            this.$store.dispatch('schedule/triggerDevice', {
              scheduleId: this.schedule.id,
              deviceId: e.device_id,
            }).catch(() => {})
          )
        );
        await this.loadSchedule();
      } finally {
        this.triggeringAll = false;
      }
    },
    entryStatusIcon(status) {
      const map = {
        pending:     'far fa-clock',
        ready:       'fas fa-bolt',
        in_progress: 'fas fa-circle-notch fa-spin',
        completed:   'fas fa-check-circle',
        failed:      'fas fa-times-circle',
        expired:     'fas fa-hourglass-end',
      };
      return map[status] || 'fas fa-circle';
    },
    entryStatusColor(status) {
      const map = {
        pending:     '#adb5bd',
        ready:       '#fb6340',
        in_progress: '#11cdef',
        completed:   '#2dce89',
        failed:      '#f5365c',
        expired:     '#f5365c',
      };
      return map[status] || '#adb5bd';
    },
    statusVariant(status) {
      return { active: 'success', completed: 'secondary', cancelled: 'danger' }[status] || 'secondary';
    },
    formatDatetime(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString();
    },
    truncate(str, len) {
      return str && str.length > len ? str.slice(0, len) + '…' : str;
    },
  },
};
</script>